import { isPlatformBrowser } from '@angular/common';
import { Component, inject, type OnInit, PLATFORM_ID } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from '@environment/environment';
import { BrandingService } from '@services/branding/branding.service';
import { ConsentService } from '@services/domain/consent.service';
import { LanguageDetectionService } from '@services/domain/language-detection.service';
import { SheetDragService } from '@services/layout/sheet-drag.service';
import { ThemeService } from '@services/layout/theme.service';
import { AuthModalComponent } from '@shared/components/auth-modal/auth-modal.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { SplashScreenComponent } from '@shared/components/splash-screen/splash-screen.component';
import { ToastComponent } from '@shared/components/toast/toast.component';
import { SharedModule } from '@shared/modules/shared.module';
import { Carousel } from 'primeng/carousel';
import { filter, firstValueFrom } from 'rxjs';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	imports: [
		RouterOutlet,
		SharedModule,
		ConfirmDialogComponent,
		ToastComponent,
		AuthModalComponent,
		SplashScreenComponent,
	],
})
export class AppComponent implements OnInit {
	currentTheme: 'light' | 'dark' = 'light';
	environment = environment;
	showSplash = true;

	private themeService = inject(ThemeService);
	private languageDetectionService = inject(LanguageDetectionService);
	private consentService = inject(ConsentService);
	private brandingService = inject(BrandingService);
	private platformId = inject(PLATFORM_ID);
	private sheetDrag = inject(SheetDragService);
	private swUpdate = inject(SwUpdate);

	constructor() {
		// Fix carousel touch behavior on mobile devices
		Carousel.prototype.onTouchMove = () => {};
	}

	async ngOnInit() {
		// Load branding first, then set language based on branding config
		const branding = await firstValueFrom(this.brandingService.load());
		await this.languageDetectionService.detectAndSetLanguage(branding?.defaultLanguage);

		this.themeService.setTheme('light');

		// Hide splash screen after everything is loaded
		setTimeout(() => {
			this.showSplash = false;
		}, 500);

		if (isPlatformBrowser(this.platformId)) {
			// Initialize services after translations are ready
			setTimeout(() => {
				this.consentService.initConsentBanner();
			}, 100);

			// Mobile bottom sheets close by dragging them down
			this.sheetDrag.init();

			// After a deploy the service worker first serves the cached version: as soon as the new one is
			// downloaded, load it (otherwise a visitor kept the old app until a second visit)
			if (this.swUpdate.isEnabled) {
				this.swUpdate.versionUpdates
					.pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
					.subscribe(() => document.location.reload());
				this.swUpdate.unrecoverable.subscribe(() => document.location.reload());
			}
		}
	}
}
